/**
 * Customer Alert History presenter. Server operational fields are authority.
 * Does not infer Pattern from recurrence labels or compute lifecycle.
 */
import {
  buyerConditionDisplayName,
  FORKLIFT_PEDESTRIAN_LABEL,
  PPE_PRESENCE_LABEL,
  RESTRICTED_AREA_LABEL,
} from '../buyerFacing/conditionDisplayNames';
import {
  canonicalConditionFamily,
  isCoreConditionFamily,
  presentCustomerPatternWindowCopy,
} from '../continuousSafety/conditionLifecyclePresentation';
import { buildConditionLifecyclePath } from '../continuousSafety/conditionLifecycleNavigation';
import { buildSpatialOperationsPath } from '../spatialOperations/spatialOperationsUrl';
import {
  resolveConfiguredCameraLabel,
  resolveConfiguredZoneLabel,
} from '../customerLabels/customerFacingControlLabel';
import { resolvePatternEvidenceCameraLabel } from '../../utils/customerPlaceLabel';
import { emailDeliveryBuyerCopy } from './liveAlertsReconciliation';
import type { BuyerLiveItem, BuyerNotificationDelivery, BuyerPatternMembership } from './liveAlertsReconciliation';

export type AlertHistoryAlertClass = 'Immediate' | 'Pattern' | 'Composite';

export type AlertHistoryDeliveryState =
  | 'NOT_CONFIGURED'
  | 'PENDING'
  | 'SENT'
  | 'FAILED'
  | 'DELIVERY_UNKNOWN'
  | 'SUPPRESSED';

export type AlertHistoryRow = {
  incidentId: string;
  recordedAt: string | null;
  timeLabel: string;
  conditionLabel: string;
  conditionFamily: string;
  cameraAreaLabel: string;
  alertClass: AlertHistoryAlertClass;
  severity: string;
  status: string;
  deliveryState: AlertHistoryDeliveryState;
  deliveryLabel: string;
  deliveryCopy: string;
  patternIdentity: string | null;
  patternMembershipSummary: string | null;
  patternSourceCount: number | null;
  ruleName: string | null;
  readableSentence: string | null;
  investigateHref: string;
  lifecycleHref: string | null;
  spatialHref: string | null;
};

export type AlertHistoryLookups = {
  cameraNameById?: Record<string, string>;
};

export type AlertHistoryListItem =
  | { kind: 'row'; row: AlertHistoryRow }
  | { kind: 'open-group'; key: string; rows: AlertHistoryRow[] };

const CONTROL_PLANE_TOKEN = /SHADOW_COMPARE|EXPERIMENTAL_SHADOW/gi;

function text(value: unknown): string {
  return String(value || '').trim();
}

function customerSafe(value: string): string {
  return text(value.replace(CONTROL_PLANE_TOKEN, '')).replace(/\s{2,}/g, ' ');
}

function severityLabel(item: BuyerLiveItem): string {
  const raw = item.operational.severity;
  if (raw && typeof raw === 'object') return customerSafe(text(raw.severity) || '—');
  return customerSafe(text(raw) || '—');
}

export function historyAlertClass(item: BuyerLiveItem): AlertHistoryAlertClass {
  const op = item.operational || ({} as BuyerLiveItem['operational']);
  const alertClass = text(op.alert_class);
  const incidentId = text(op.incident_id);
  const incidentType = text(op.incident_type);
  if (
    alertClass === 'Composite'
    || text(op.event_kind) === 'COMPOSITE_ALERT'
    || text(op.trigger_kind) === 'COMPOSITE'
    || incidentType === 'composite_alert'
    || incidentId.startsWith('ci1_')
  ) {
    return 'Composite';
  }
  if (alertClass === 'Pattern') return 'Pattern';
  if (alertClass === 'Immediate') return 'Immediate';
  if (text(op.event_kind) === 'PATTERN_ALERT' || text(op.trigger_kind) === 'TEMPORAL_PATTERN') {
    return 'Pattern';
  }
  return 'Immediate';
}

export function historyDeliveryState(delivery?: BuyerNotificationDelivery | null): AlertHistoryDeliveryState {
  if (!delivery) return 'NOT_CONFIGURED';
  if (delivery.configured === false || delivery.state === 'NOT_CONFIGURED') return 'NOT_CONFIGURED';
  const state = text(delivery.state).toUpperCase();
  if (state === 'PENDING' || state === 'LEASED' || state === 'SENDING' || state === 'RETRY_WAIT') return 'PENDING';
  if (state === 'SENT') return 'SENT';
  if (state === 'DELIVERY_UNKNOWN') return 'DELIVERY_UNKNOWN';
  if (state === 'FAILED_RETRYABLE' || state === 'FAILED_FINAL' || state === 'FAILED') return 'FAILED';
  if (state === 'SUPPRESSED') return 'SUPPRESSED';
  return 'NOT_CONFIGURED';
}

export function historyDeliveryLabel(state: AlertHistoryDeliveryState): string {
  if (state === 'NOT_CONFIGURED') return 'Not configured';
  if (state === 'PENDING') return 'Queued';
  if (state === 'SENT') return 'Sent';
  if (state === 'FAILED') return 'Failed';
  if (state === 'SUPPRESSED') return 'Held by timing';
  return 'Delivery unknown';
}

function conditionLabelFor(family: string, incidentType?: string | null): string {
  if (family === 'RESTRICTED_AREA_ENTRY') return RESTRICTED_AREA_LABEL;
  if (family === 'PERSON_FORKLIFT_PROXIMITY') return FORKLIFT_PEDESTRIAN_LABEL;
  if (family === 'PPE_NON_COMPLIANCE') return PPE_PRESENCE_LABEL;
  return customerSafe(buyerConditionDisplayName(undefined, incidentType));
}

function patternIdentity(item: BuyerLiveItem, alertClass: AlertHistoryAlertClass): string | null {
  if (alertClass !== 'Pattern') return null;
  const op = item.operational;
  const membership = op.pattern_membership;
  const patternId = text(op.subject?.pattern_id || membership?.pattern_id);
  const version = op.subject?.pattern_version ?? membership?.pattern_version;
  if (patternId && version != null && text(String(version))) return customerSafe(`${patternId} · v${version}`);
  if (patternId) return customerSafe(patternId);
  return null;
}

function historyCameraAreaLabel(item: BuyerLiveItem, lookups: AlertHistoryLookups = {}): string {
  const op = item.operational;
  const compositeLocation = customerSafe(text(op.composite_location_display));
  if (compositeLocation && compositeLocation.toLowerCase() !== 'worksafety') return compositeLocation;
  const cameraId = text(op.camera_id);
  const zoneId = text(op.zone_id);
  const cameraName = lookups.cameraNameById?.[cameraId];
  const labeled = resolvePatternEvidenceCameraLabel({
    cameraId,
    cameraName,
    zoneId,
    zoneName: resolveConfiguredZoneLabel(zoneId),
  });
  if (labeled && labeled !== 'Camera name unavailable') return customerSafe(labeled);
  const configured = resolveConfiguredCameraLabel(cameraId);
  if (configured) return customerSafe(configured);
  const zone = resolveConfiguredZoneLabel(zoneId);
  if (zone) return customerSafe(zone);
  return customerSafe(cameraName || '—');
}

function isOpenHistoryStatus(status: string): boolean {
  return text(status).toUpperCase() === 'OPEN';
}

function openRepeatKey(row: AlertHistoryRow): string {
  return `${row.conditionFamily}\0${row.cameraAreaLabel}\0${row.alertClass}`;
}

export function splitHistoryByLocalDay(
  rows: AlertHistoryRow[],
  now: Date = new Date(),
): { recent: AlertHistoryRow[]; earlier: AlertHistoryRow[] } {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const recent: AlertHistoryRow[] = [];
  const earlier: AlertHistoryRow[] = [];
  for (const row of rows) {
    const ts = row.recordedAt ? Date.parse(row.recordedAt) : NaN;
    if (!Number.isFinite(ts) || ts >= start) recent.push(row);
    else earlier.push(row);
  }
  return { recent, earlier };
}

export function groupRepeatedOpenHistory(rows: AlertHistoryRow[]): AlertHistoryListItem[] {
  const buckets = new Map<string, AlertHistoryRow[]>();
  for (const row of rows) {
    if (!isOpenHistoryStatus(row.status) || row.alertClass === 'Composite') continue;
    const key = openRepeatKey(row);
    const members = buckets.get(key) || [];
    members.push(row);
    buckets.set(key, members);
  }
  const seen = new Set<string>();
  const items: AlertHistoryListItem[] = [];
  for (const row of rows) {
    if (!isOpenHistoryStatus(row.status) || row.alertClass === 'Composite') {
      items.push({ kind: 'row', row });
      continue;
    }
    const key = openRepeatKey(row);
    if (seen.has(key)) continue;
    seen.add(key);
    const members = buckets.get(key) || [row];
    if (members.length === 1) items.push({ kind: 'row', row: members[0] });
    else items.push({ kind: 'open-group', key, rows: members });
  }
  return items;
}

function patternMembershipSummary(membership?: BuyerPatternMembership | null): string | null {
  if (!membership) return null;
  const windowCopy = presentCustomerPatternWindowCopy(membership.min_events, membership.window_seconds);
  const observed = membership.observed_count == null ? null : `${membership.observed_count} contributing`;
  const parts = [windowCopy, observed].filter(Boolean);
  return parts.length ? customerSafe(parts.join(' · ')) : null;
}

export function presentAlertHistoryRow(
  item: BuyerLiveItem,
  lookups: AlertHistoryLookups = {},
): AlertHistoryRow | null {
  const op = item?.operational;
  const incidentId = text(op?.incident_id);
  if (!incidentId) return null;
  if (op.excluded === true) return null;
  const alertClass = historyAlertClass(item);
  if (alertClass === 'Composite' && text(op.buyer_projection_status) === 'COMPOSITE_BUYER_PROJECTION_MALFORMED') {
    return null;
  }
  const deliveryState = historyDeliveryState(op.notification_delivery);
  const recordedAt = alertClass === 'Composite'
    ? (text(op.composite_triggered_at) || text(op.recorded_at) || text(op.updated_at) || null)
    : (text(op.recorded_at) || text(op.updated_at) || null);
  const timeLabel = recordedAt
    ? new Date(recordedAt).toLocaleString()
    : '—';
  const compositeSubject = text(op.subject_kind) === 'COMPOSITE' || text(op.subject_type) === 'COMPOSITE';
  const conditionFamily = alertClass === 'Composite' || (alertClass === 'Pattern' && compositeSubject)
    ? 'COMPOSITE_ALERT'
    : canonicalConditionFamily(op.condition_type || op.incident_type);
  const conditionLabel = alertClass === 'Composite' || (alertClass === 'Pattern' && compositeSubject)
    ? customerSafe(text(op.composite_rule_name) || (alertClass === 'Pattern' ? 'Composite' : 'Composite Alert'))
    : conditionLabelFor(conditionFamily, op.incident_type);
  const cameraAreaLabel = historyCameraAreaLabel(item, lookups);
  const siteId = text(op.site_id);
  const lifecycleHref = alertClass !== 'Composite' && siteId && isCoreConditionFamily(conditionFamily)
    ? buildConditionLifecyclePath({
      siteId,
      conditionId: conditionFamily,
      cameraId: op.camera_id,
      zoneId: op.zone_id,
      orgId: op.organization_id,
    })
    : null;
  const spatialHref = alertClass !== 'Composite' && siteId
    ? buildSpatialOperationsPath({
      siteId,
      cameraId: op.camera_id,
      conditionId: isCoreConditionFamily(conditionFamily) ? conditionFamily : null,
    })
    : null;
  return {
    incidentId,
    recordedAt,
    timeLabel: customerSafe(timeLabel),
    conditionLabel,
    conditionFamily,
    cameraAreaLabel,
    alertClass,
    severity: severityLabel(item),
    status: customerSafe(text(op.operational_status) || 'OPEN'),
    deliveryState,
    deliveryLabel: historyDeliveryLabel(deliveryState),
    deliveryCopy: customerSafe(emailDeliveryBuyerCopy(op.notification_delivery)),
    patternIdentity: patternIdentity(item, alertClass),
    patternMembershipSummary: patternMembershipSummary(op.pattern_membership),
    patternSourceCount: op.pattern_membership?.source_occurrence_count ?? null,
    ruleName: alertClass === 'Composite' || (alertClass === 'Pattern' && compositeSubject)
      ? customerSafe(text(op.composite_rule_name) || 'Composite Alert')
      : null,
    readableSentence: alertClass === 'Composite' ? customerSafe(text(op.composite_readable_sentence)) || null : null,
    investigateHref: alertClass === 'Composite' || (alertClass === 'Pattern' && compositeSubject)
      ? `/live?incident_id=${encodeURIComponent(incidentId)}`
      : `/incidents?incident_id=${encodeURIComponent(incidentId)}&review=1`,
    lifecycleHref,
    spatialHref,
  };
}
